import React from 'react';
import {useWindowDimensions, View} from 'react-native';

import Text from '/component/base/Text';
import {BonusesID} from '/constant/TestIDs';
import {Shared} from '/constant/TestIDs';
import {Rank} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';
import testProperties from '/util/testing/testProperties';

import {InlineFormattedNumber} from '../FormattedNumber';

import {
  getCurrentMonthName,
  getFormattedMonthName,
} from './BBBBannerProgressBar/BBBBannerProgressBar.util';
import {styles} from './BonusProgressBar.style';
import {getDaysLeftForBonus} from './BonusProgressBar.util';
import {CoachRankMonthBox} from './CoachRankMonthBox';

interface BBBonusContributor {
  isEligible: boolean;
  bonusMonth: string;
  fcv: number;
}

interface BBBProfileCardProgressBarProps {
  currentDate: string;
  activationDate: string;
  BBBonusContributors: BBBonusContributor[];
}

export const BBBProfileCardProgressBar: React.FC<
  BBBProfileCardProgressBarProps
> = ({currentDate = '', activationDate = '', BBBonusContributors = []}) => {
  const {fontScale} = useWindowDimensions();
  const {tt} = useTranslation('BBBProfileCardProgressBar');
  const dateUtil = getDateUtilDefaultInstance();
  const daysLeftForBonus = getDaysLeftForBonus({
    currentDate,
  });
  const currentMonth = getCurrentMonthName(currentDate);

  const eligibleBonusMonths = dateUtil.getMonthNames(
    activationDate,
    currentDate,
  );

  const boxesArray: any = [];
  Array.isArray(BBBonusContributors) &&
    BBBonusContributors?.map(({isEligible, bonusMonth, fcv}, _idx) => {
      const formattedMonth = getFormattedMonthName(bonusMonth);
      if (isEligible && eligibleBonusMonths?.includes(formattedMonth)) {
        boxesArray.push({isChecked: true, bonusMonth: formattedMonth, fcv});
      } else if (isEligible && !eligibleBonusMonths?.includes(formattedMonth)) {
        boxesArray.push({
          isChecked: false,
          isEligibleFutureMonth: true,
          bonusMonth: formattedMonth,
          fcv,
        });
      } else {
        boxesArray.push({
          isChecked: false,
          noStyle: true,
          bonusMonth: formattedMonth,
          fcv,
        });
      }
    });

  const getDaysLeftText = () => {
    if (daysLeftForBonus > 1) {
      return `${daysLeftForBonus} days left`;
    } else if (daysLeftForBonus === 1) {
      return `${daysLeftForBonus} day left`;
    } else {
      return 'last day';
    }
  };

  return (
    <View>
      <View style={styles.boxesContainer}>
        {boxesArray?.map(
          // @ts-expect-error TS(7031): Binding element 'isChecked' implicitly has an 'any... Remove this comment to see the full error message
          ({isChecked, bonusMonth, isEligibleFutureMonth}, index) => (
            <CoachRankMonthBox
              isChecked={isChecked}
              isEligibleFutureMonth={isEligibleFutureMonth}
              enableFontResize
              fontScale={fontScale}
              month={bonusMonth}
              key={index}
            />
          ),
        )}
      </View>
      {Array.isArray(BBBonusContributors) &&
        // @ts-expect-error TS(7030): Not all code paths return a value.
        BBBonusContributors?.map(({fcv, isEligible, bonusMonth}, _index) => {
          if (
            isEligible &&
            eligibleBonusMonths?.includes(getFormattedMonthName(bonusMonth))
          ) {
            return (
              <View style={styles.fcvContainer}>
                <View style={styles.bbbFcvText}>
                  {tt('eligibleFcvMonth', () => (
                    <Text
                      allowFontScaling
                      maxFontSizeMultiplier={2}
                      style={styles.moneyBold}
                      {...testProperties(Rank.rankMonthEarned)}>
                      {bonusMonth}
                    </Text>
                  ))}
                  {bonusMonth === currentMonth &&
                    tt('daysLeft', () => (
                      <Text
                        allowFontScaling
                        maxFontSizeMultiplier={2}
                        style={styles.bbbDaysLeft}
                        emphasis="medium"
                        variant="caption"
                        {...testProperties(Shared.daysLeft)}>
                        {getDaysLeftText()}
                      </Text>
                    ))}
                </View>

                {tt('fcvEarned', () => (
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    style={styles.moneyBold}
                    testID={BonusesID.earnedFcvValue}>
                    <InlineFormattedNumber
                      textEmphasis="high"
                      value={fcv}
                      truncate
                    />{' '}
                    FCV
                  </Text>
                ))}
              </View>
            );
          }
        })}
    </View>
  );
};
